import { type Child, type SessionInfo } from '../api'
import { ExitButton } from '../components/ExitButton'
import { radius, theme, TAP_MIN } from '../theme'
import { Button, Card, Heading, Page } from '../components/ui'

/**
 * "Today's quests" — what is coming, before it starts.
 *
 * Worth a whole screen because it answers the question a child asks first:
 * how long is this going to take? A session that starts straight into a
 * question feels like it might never end.
 */
const STOPS: Record<string, { title: string; emoji: string; perQuestion: number }> = {
  spelling: { title: 'Spelling and listening', emoji: '🔤', perQuestion: 20 },
  meaning:  { title: 'Word puzzles',           emoji: '🧩', perQuestion: 30 },
  reading:  { title: 'A story to read',        emoji: '📖', perQuestion: 150 },
  writing:  { title: 'Write something',        emoji: '✏️', perQuestion: 90 },
  play:     { title: 'Play',                   emoji: '🎲', perQuestion: 60 },
}

export function SessionMap({
  child, session, onStart, onHome, onChangeInterests,
}: {
  child: Child
  session: SessionInfo
  onStart: () => void
  onHome: () => void
  onChangeInterests: () => void
}) {
  const stops = session.map.map((stop) => {
    const meta = STOPS[stop.module] ?? { title: stop.module, emoji: '•', perQuestion: 30 }
    return { ...stop, ...meta, seconds: stop.count * meta.perQuestion }
  })
  const totalMinutes = Math.max(1, Math.round(
    stops.reduce((n, s) => n + s.seconds, 0) / 60,
  ))
  const doneSoFar = session.cursor

  return (
    <Page width={560}>
      <Heading sub={session.resumed
        ? `You stopped at question ${doneSoFar + 1}. Carry on from there.`
        : `About ${totalMinutes} minutes, ${session.planned_count} questions.`}>
        {session.resumed ? 'Carry on' : `Today's quests, ${child.display_name}`}
      </Heading>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {stops.map((stop, i) => {
          const finished = session.cursor > stop.end
          const current = session.cursor >= stop.start && session.cursor <= stop.end
          return (
            <Card
              key={i}
              style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: 18,
                borderColor: current ? theme.orange : theme.borderWarm,
                background: finished ? theme.greenPale : theme.card,
                opacity: finished ? 0.75 : 1,
              }}
            >
              <div style={{
                width: 44, height: 44, borderRadius: radius.pill, flexShrink: 0,
                background: finished ? theme.green
                  : current ? theme.orangePale : theme.bg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20,
              }}>
                {finished ? '✓' : stop.emoji}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 17, fontWeight: 700, color: theme.text }}>
                  {i + 1}. {stop.title}
                </div>
                <div style={{ fontSize: 14, color: theme.faint, marginTop: 2 }}>
                  {stop.count} {stop.count === 1 ? 'question' : 'questions'}
                  {' · about '}{Math.max(1, Math.round(stop.seconds / 60))} min
                </div>
              </div>
              {current && !finished ? (
                <span style={{
                  fontSize: 12, fontWeight: 800, color: theme.orangeDark,
                  background: theme.orangeTint, borderRadius: radius.pill,
                  padding: '5px 10px', whiteSpace: 'nowrap',
                }}>
                  NEXT
                </span>
              ) : null}
            </Card>
          )
        })}
      </div>

      <div style={{ marginTop: 22 }}>
        <Button full tone="child" onClick={onStart}>
          {session.resumed ? 'Carry on' : 'Start'}
        </Button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
        {/* Between the monthly check-ins, so a child who suddenly gets into
            dinosaurs does not have to wait three weeks. */}
        <button
          type="button"
          onClick={onChangeInterests}
          style={{
            fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: theme.muted,
            background: 'transparent', border: `1px solid ${theme.border}`,
            borderRadius: radius.sm, padding: '8px 14px',
            minHeight: TAP_MIN, cursor: 'pointer',
          }}
        >
          Change what I like
        </button>
        <ExitButton onExit={onHome} />
      </div>
    </Page>
  )
}
